import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableIconCell,
  Pagination,
  ContainerAvatar,
  sendSignUpModal,
  TableIconsCell,
  Button,
  FlagIcon,
  Chip,
  sendSignUpSuccessModal,
  SimpleTable,
} from '../../design-system';
import { DashboardHeader, FilterIconButton, Cards } from '../dashboard';
import { Filters } from './Filters';
import { MobileFilters } from './MobileFilters';
import { Card } from './Cards';
import { formatContainerType, momentTextFormat } from '../../utils';
import {
  useStatesWithFilterCounter,
  usePaginationState,
  useSalesByClientQuery,
  smallScreenSize,
  useIsSmallScreen,
} from '../../hooks';
import { i18n } from '../../i18n';
import { TABLE, ContainerConditionToChipLabels, ContainerConditionToChipColors } from '../../const';

let openFilterPopup: any = () => null;

export const MarketplaceWrapper = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  height: 100%;

  @media (min-width: ${smallScreenSize + 1}px) {
    & > *:nth-child(1) > * {
      max-width: initial;
      padding: 0 var(--spacing-10);
    }

    & > *:nth-child(2) {
      padding: var(--spacing-10);
      padding-top: var(--spacing-8);
    }
  }

  @media (max-width: ${smallScreenSize}px) {
    & > *:nth-child(1) {
      padding: 0 var(--spacing-6);
    }

    & > *:nth-child(2) {
      padding: var(--spacing-3) var(--spacing-6);
      grid-row-gap: var(--spacing-3);
    }
  }
`;

interface MarketplacePageProps {
  innerScroll?: boolean;
}

export const MarketplacePage = styled.div<MarketplacePageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;
  position: relative;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}

  @media (max-width: ${smallScreenSize}px) {
    grid-row-gap: var(--spacing-3);

    & > nav > * {
      justify-self: center;
    }
  }
`;

export const MarketplaceHeader = ({ totalCount }: any) => {
  const isSmallScreen = useIsSmallScreen();

  return (
    <DashboardHeader isSmallScreen={isSmallScreen}>
      <Typography variant={isSmallScreen ? 'textL' : 'textXL'} weight={'bold'}>
        {!isSmallScreen && i18n.t('pages.marketplace.title', { totalCount })}
        {isSmallScreen && i18n.t('pages.marketplace.shortTitle', { totalCount })}
      </Typography>
      {isSmallScreen && <FilterIconButton onClick={openFilterPopup} />}
    </DashboardHeader>
  );
};

export const MarketplaceBody = ({ setTotalCount, handleClickImage }: any) => {
  const params: any = useParams();
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const isSmallScreen = useIsSmallScreen();

  const { page, take, skip, handlePageChange } = usePaginationState();
  const [states, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      country: '',
      city: '',
      code: '',
      number: '',
      type: 'All',
      condition: 'All',
    },
    { code: params.code },
  );

  const { data = { data: [], totalCount: 0 }, isFetching } = useSalesByClientQuery({
    ...filteredStates,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    openFilterPopup = () => setIsOpenFilter(true);
    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleOnPageChange = (e: any, page: number) => {
    handlePageChange(e, page);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handleOnPageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (row: any, number: any, val: any) => {
    if (val === 'buy') {
      sendSignUpModal.price = row.sale.finalPrice;
      sendSignUpModal.number = number;
      sendSignUpModal.withCategory = false;
      sendSignUpModal.submitClick = () => {
        sendSignUpSuccessModal.open();
      };
      sendSignUpModal.open();
    }
  };

  if (isSmallScreen)
    return (
      <>
        <Cards>
          {data.data.map((container, i) => (
            <Card
              key={i}
              handleClickImage={handleClickImage}
              handleDotsOptionSelect={handleDotsOptionSelect}
              isCardImageClick
              {...container}
            />
          ))}
        </Cards>
        <Pagination
          count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
          siblingCount={0}
          page={page}
          onChange={handleOnPageChange}
        />
        <MobileFilters
          isOpen={isOpenFilter}
          setIsOpen={setIsOpenFilter}
          setStates={handleFilterChange}
          states={states}
          {...filteredStates}
        />
      </>
    );

  return (
    <SimpleTable
      data={data}
      filters={() => <Filters setStates={handleFilterChange} {...states} />}
      tableRowProps={(container, i) => ({
        key: container.number + i,
        onClick: () => handleClickImage(container.images),
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('label.photo'),
          body: (container) => (
            <TableIconCell component={'th'} align={'left'}>
              <ContainerAvatar
                images={container.images}
                onClick={() => handleClickImage(container.images)}
              />
            </TableIconCell>
          ),
        },
        {
          headText: () => i18n.t('label.containerNumber'),
          bodyText: (container) => container.number,
        },
        {
          headText: () => i18n.t('label.type'),
          bodyText: (container) => formatContainerType(container.type),
        },
        {
          headText: () => i18n.t('label.condition'),
          body: (container) => (
            <TableCell align={'left'}>
              {container.condition && (
                <Chip
                  size={'small'}
                  label={ContainerConditionToChipLabels[container.condition]}
                  color={ContainerConditionToChipColors[container.condition]}
                />
              )}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.city'),
          body: (container) => (
            <TableIconsCell align={'left'} withGap>
              <FlagIcon country={container.clientYard.yard.address.country} />
              {container.clientYard.yard.address.city}
            </TableIconsCell>
          ),
        },
        {
          headText: () => i18n.t('label.price'),
          bodyText: (container) => `${container.sale.finalPrice}$`,
        },
        {
          headText: () => i18n.t('label.forSaleDate'),
          bodyText: (container) =>
            container.sale.forSaleDate
              ? momentTextFormat(container.sale.forSaleDate, { type: 'client' } as any)
              : '',
        },
        {
          headText: () => i18n.t('label.buyAction'),
          body: (container) => (
            <TableCell align={'left'}>
              <Button
                size={'small'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDotsOptionSelect(container, container.number, 'buy');
                }}
              >
                {i18n.t('button.addToCart')}
              </Button>
            </TableCell>
          ),
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handleOnPageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
